import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FloatingNavigation from "../components/FloatingNavigation";

const GuideAnalyticsPage = () => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState({
    totalRevenue: 0,
    totalCommission: 0,
    totalTickets: 0,
    activeGuides: 0
  });
  const [guidePerformance, setGuidePerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    guideId: ""
  });
  const [sortBy, setSortBy] = useState("totalSales");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("No authentication token found. Please login again.");
        return;
      }

      console.log("Fetching statistics with params:", filters);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/guide-analytics/statistics`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: filters
        }
      );

      console.log("Statistics response:", response.data);
      setStatistics(response.data);
    } catch (err) {
      console.error("Error fetching statistics:", err);
      if (err.response) {
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
        setError(`Failed to fetch statistics: ${err.response.data?.message || err.response.statusText}`);
      } else {
        setError("Failed to fetch statistics");
      }
    }
  }, [filters]);

  const fetchGuidePerformance = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      console.log("Fetching guide performance with params:", {
        ...filters,
        sortBy,
        sortOrder
      });

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/guide-analytics/performance`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            ...filters,
            sortBy,
            sortOrder
          }
        }
      );

      console.log("Guide performance response:", response.data);
      setGuidePerformance(response.data || []);
      setError("");
    } catch (err) {
      console.error("Error fetching guide performance:", err);
      if (err.response) {
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
        setError(`Failed to fetch guide performance data: ${err.response.data?.message || err.response.statusText}`);
      } else {
        setError("Failed to fetch guide performance data");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    fetchStatistics();
    fetchGuidePerformance();
  }, [fetchStatistics, fetchGuidePerformance]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleGuideClick = (guideId) => {
    navigate(`/guide-sales/${guideId}`);
  };

  const exportToCSV = () => {
    const csvData = guidePerformance.map(guide => ({
      "Guide ID": guide.guideId,
      "Guide Name": guide.guideName,
      "Total Sales": guide.totalSales,
      "Tickets Sold": guide.ticketsSold,
      "Commission (20%)": guide.commission,
      "Last Sale Date": guide.lastSaleDate ? new Date(guide.lastSaleDate).toISOString().split('T')[0] : "N/A"
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map(row => Object.values(row).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guide_performance_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && guidePerformance.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans text-slate-700">
        <FloatingNavigation />
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-slate-500 text-sm">Loading guide analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-700">
      <FloatingNavigation />
      <div className="bg-white border-b border-slate-200 py-8">
        <div className="w-full max-w-[1600px] mx-auto px-6 flex justify-between items-center flex-wrap gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Guide Analytics</h1>
            <p className="text-base text-slate-500">Monitor guide performance and track commission earnings</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-700 transition-all duration-150"
              onClick={() => navigate("/view-guides")}
            >
              View All Guides
            </button>
            <button 
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-blue-500 bg-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 transition-all duration-150"
              onClick={() => navigate("/add-guide")}
            >
              Add New Guide
            </button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1600px] mx-auto py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mx-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow duration-150 relative min-h-[120px] flex flex-col">
            <div className="absolute top-6 right-6">
              <span className="text-xs font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">+12.5%</span>
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">TOTAL REVENUE</div>
            <div className="text-3xl font-bold text-slate-800 mb-2">LKR {statistics.totalRevenue?.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-auto">Revenue generated this period</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow duration-150 relative min-h-[120px] flex flex-col">
            <div className="absolute top-6 right-6">
              <span className="text-xs font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">+8.2%</span>
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">TOTAL COMMISSION</div>
            <div className="text-3xl font-bold text-slate-800 mb-2">LKR {statistics.totalCommission?.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-auto">20% commission earned</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow duration-150 relative min-h-[120px] flex flex-col">
            <div className="absolute top-6 right-6">
              <span className="text-xs font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">+15.1%</span>
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">TOTAL TICKETS</div>
            <div className="text-3xl font-bold text-slate-800 mb-2">{statistics.totalTickets?.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-auto">Tickets sold by guides</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-violet-500 shadow-sm hover:shadow-md transition-shadow duration-150 relative min-h-[120px] flex flex-col">
            <div className="absolute top-6 right-6">
              <span className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-600">0%</span>
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">ACTIVE GUIDES</div>
            <div className="text-3xl font-bold text-slate-800 mb-2">{statistics.activeGuides}</div>
            <div className="text-xs text-slate-500 mt-auto">Currently active guides</div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mx-6 mb-8 flex gap-6 items-end flex-wrap">
          <div className="flex flex-col gap-2 flex-1 min-w-40">
            <label className="text-sm font-medium text-slate-700">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
            />
          </div>
          <div className="flex flex-col gap-2 flex-1 min-w-40">
            <label className="text-sm font-medium text-slate-700">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
            />
          </div>
          <div className="flex flex-col gap-2 flex-1 min-w-40">
            <label className="text-sm font-medium text-slate-700">Guide ID</label>
            <input
              type="text"
              placeholder="Enter guide ID"
              value={filters.guideId}
              onChange={(e) => handleFilterChange("guideId", e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
            />
          </div>
          <div className="flex items-end flex-shrink-0">
            <button 
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border border-blue-500 bg-white text-blue-500 hover:bg-blue-500 hover:text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={exportToCSV}
              disabled={guidePerformance.length === 0}
            >
              Export CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mx-6 mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mx-6 mb-8">
          <div className="px-6 py-6 border-b border-slate-200 bg-slate-50">
            <h2 className="text-xl font-semibold text-slate-800 mb-1">Guide Performance</h2>
            <p className="text-sm text-slate-500">Sorted by highest sales performance</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap">Rank</th>
                  <th 
                    onClick={() => handleSort("guideId")} 
                    className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100"
                  >
                    Guide ID
                    {sortBy === "guideId" && (
                      <span className={`ml-1 text-blue-500 text-xs`}>
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th 
                    onClick={() => handleSort("guideName")} 
                    className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100"
                  >
                    Guide Name
                    {sortBy === "guideName" && (
                      <span className={`ml-1 text-blue-500 text-xs`}>
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th 
                    onClick={() => handleSort("totalSales")} 
                    className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100"
                  >
                    Total Sales
                    {sortBy === "totalSales" && (
                      <span className={`ml-1 text-blue-500 text-xs`}>
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th 
                    onClick={() => handleSort("ticketsSold")} 
                    className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100"
                  >
                    Tickets Sold
                    {sortBy === "ticketsSold" && (
                      <span className={`ml-1 text-blue-500 text-xs`}>
                        {sortOrder === "asc" ? "↓" : "↑"}
                      </span>
                    )}
                  </th>
                  <th 
                    onClick={() => handleSort("commission")} 
                    className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100"
                  >
                    Commission (20%)
                    {sortBy === "commission" && (
                      <span className={`ml-1 text-blue-500 text-xs`}>
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap">Last Sale</th>
                  <th className="bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 text-xs uppercase tracking-wide border-b border-slate-200 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {guidePerformance.map((guide, index) => (
                  <tr 
                    key={guide.guideId} 
                    className="cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    onClick={() => handleGuideClick(guide.guideId)}
                  >
                    <td className="px-4 py-4 align-middle">
                      <span className={`inline-flex items-center justify-center min-w-8 h-6 px-2 rounded-xl text-xs font-semibold ${
                        index < 3 
                          ? 'bg-amber-100 text-amber-700' 
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        #{index + 1}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-middle">{guide.guideId}</td>
                    <td className="px-4 py-4 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-sm">
                          {guide.guideName.charAt(0).toUpperCase()}
                        </div>
                        <span>{guide.guideName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="text-slate-500 text-xs mr-1">LKR</span>
                      <span className="font-semibold text-slate-800">{guide.totalSales?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="font-semibold text-blue-600">{guide.ticketsSold}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="text-slate-500 text-xs mr-1">LKR</span>
                      <span className="font-semibold text-emerald-600">{guide.commission?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      {guide.lastSaleDate 
                        ? new Date(guide.lastSaleDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
                        : "No sales"
                      }
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <button 
                        className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-700 transition-all duration-150"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGuideClick(guide.guideId);
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {guidePerformance.length === 0 && (
              <div className="text-center py-12 px-6 text-slate-500">
                <p className="text-base">No guide performance data available for the selected period.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideAnalyticsPage;
